
import { useState, useEffect } from 'react';
import { ArrowLeft, Menu, Plus, Copy, Bell, Search, MessageSquare , ChevronLeft} from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuth } from "@/hooks/useAuth";
import { useUnreadMessageCount } from "@/hooks/useUnreadMessageCount";
import { useNotificationsContext } from "@/contexts/NotificationsContext";
import { useAppTranslations } from '@/hooks/useAppTranslations';
import { getInitials } from '@/utils/getDisplayName';


export function SmartTopNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const { unreadCount: unreadMessageCount } = useUnreadMessageCount();
  const { unreadCount: unreadNotificationCount } = useNotificationsContext();
  const { t } = useAppTranslations();
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const checkModalState = () => setIsModalOpen(document.body.hasAttribute('data-modal-open'));
    checkModalState();
    const observer = new MutationObserver(checkModalState);
    observer.observe(document.body, { attributes: true, attributeFilter: ['data-modal-open'] });
    return () => observer.disconnect();
  }, []);

  if (isModalOpen) return null;

  // Smart back button logic based on current route
  const getBackRoute = () => {
    const path = location.pathname;
    
    // Solo/Multiplayer game routes go back to Play Hub
    if (path.includes('/play/solo/') || path.includes('/play/multiplayer/')) {
      if (path.includes('/score')) return '/play';
      if (path.includes('/lobby')) return '/play';
      if (path.includes('/complete')) return '/play';
      if (path.includes('/join')) return '/play';
    }
    
    // Tournament routes have specific back logic
    if (path.includes('/tournament/')) {
      if (path.includes('/score') || path.includes('/leaderboard')) {
        // Extract tournament ID for overview route
        const tournamentId = path.split('/tournament/')[1]?.split('/')[0];
        return `/tournament/${tournamentId}/overview`;
      }
      if (path.includes('/overview')) return '/tournaments';
      if (path.includes('/teams')) {
        const tournamentId = path.split('/tournament/')[1]?.split('/')[0];
        return `/tournament/${tournamentId}/overview`;
      }
    }
    
    // Default back routes
    if (path.startsWith('/tournaments')) return '/dashboard';
    if (path.startsWith('/play')) return '/dashboard';
    if (path.startsWith('/social')) return '/dashboard';
    if (path === '/profile') return '/dashboard';
    
    return '/dashboard';
  };

  // Get page title based on route
  const getPageTitle = () => {
    const path = location.pathname;
    
    // Dashboard routes
    if (path === '/dashboard') return t.navigation.dashboard;
    if (path === '/dashboard/enhanced-bold') return t.navigation.dashboard;
    
    // Play routes
    if (path === '/play') return t.navigation.play;
    if (path === '/play/add-past-round') return t.navigation.addPastRound;
    if (path === '/play/clubs') return t.navigation.browseClubs;
    if (path === '/play/solo') return t.navigation.soloRound;
    if (path.includes('/play/solo/') && path.includes('/score')) return t.navigation.scoreEntry;
    if (path.includes('/play/solo/') && path.includes('/celebration')) return t.navigation.roundComplete;
    if (path.includes('/play/solo/') && path.includes('/complete')) return t.navigation.roundComplete;
    // /play/multiplayer route removed - redirects handled elsewhere
    if (path.includes('/play/multiplayer/') && path.includes('/create')) return t.navigation.createGame;
    if (path.includes('/play/multiplayer/') && path.includes('/lobby')) return t.navigation.gameLobby;
    if (path.includes('/play/multiplayer/') && path.includes('/score')) return t.navigation.scoreEntry;
    if (path.includes('/play/multiplayer/') && path.includes('/complete')) return t.navigation.gameComplete;
    if (path.includes('/play/multiplayer/join')) return t.navigation.joinGame;
    
    // Club routes
    if (path === '/club') return t.navigation.club;
    if (path === '/club-old') return t.navigation.clubHub;
    if (path === '/club/activity') return t.navigation.clubActivity;
    if (path.includes('/club/') && path.includes('/details')) return t.navigation.clubDetails;
    if (path.includes('/club/') && path.includes('/members')) return t.navigation.members;
    if (path === '/club/chat') return t.navigation.clubChat;
    if (/^\/club\/[^/]+\/events$/.test(path)) return t.navigation.events;
    if (/^\/club\/[^/]+\/practice-board$/.test(path)) return t.practiceWeek.title;
    if (/^\/club\/[^/]+\/my-events$/.test(path)) return t.navigation.myEvents;
    
    // Tournament routes
    if (path.includes('/tournament/') && path.includes('/scores')) return t.navigation.scoreEntry;
    if (path.includes('/tournament/') && path.includes('/leaderboard')) return t.navigation.leaderboard;
    if (path.includes('/tournament/') && path.includes('/overview')) return t.navigation.tournament;
    
    // Profile routes
    if (path === '/profile') return t.navigation.profile;
    if (path === '/profile/trophy-room') return t.navigation.trophyRoom;
    if (path === '/profile/statistics') return t.navigation.statistics;
    if (path === '/profile/courses') return t.navigation.courses;
    if (path === '/profile/activities') return t.navigation.activities;
    if (path === '/profile/posts') return t.navigation.posts;
    if (path === '/profile/performance-zones') return t.navigation.performanceZones;
    if (path === '/profile/clubs') return t.navigation.myClubs;
    if (path === '/profile/invite-friends') return t.navigation.inviteFriends;
    if (path === '/profile/friends') return t.navigation.friends;
    if (path === '/profile/feedback') return t.navigation.feedback;
    if (path === '/profile/expenses') return t.navigation.expenses;
    if (path === '/profile/tournaments') return t.navigation.tournament;
    // Another member's profile — after every exact /profile/... route above
    if (/^\/profile\/[^/]+$/.test(path)) return t.navigation.profile;
    
    // Course routes
    if (path.includes('/course/') && path.includes('/leaderboards')) return t.navigation.courseLeaderboard;
    if (path.includes('/course/') && path.includes('/records/')) return t.navigation.courseRecords;
    if (path.includes('/course/') && path.includes('/rounds')) return t.navigation.roundHistory;
    if (path.includes('/course/') && path.includes('/analytics')) return t.navigation.courseAnalytics;
    if (path.match(/^\/course\/[^\/]+$/)) return t.navigation.courseHub;
    
    // Leaderboard routes
    if (path === '/leaderboard') return t.navigation.leaderboard;
    if (path === '/leaderboard-activity') return t.navigation.activityLeaderboard;
    if (path === '/leaderboard-v1') return t.navigation.leaderboard;
    if (path === '/leaderboard-v2') return t.navigation.leaderboard;
    if (path === '/leaderboard-v3') return t.navigation.leaderboard;
    if (path === '/leaderboard-activity-v1') return t.navigation.activityLeaderboard;
    if (path === '/leaderboard-activity-v2') return t.navigation.activityLeaderboard;
    if (path === '/leaderboard-activity-v3') return t.navigation.activityLeaderboard;
    if (path === '/leaderboard-refined-1') return t.navigation.leaderboard;
    if (path === '/leaderboard-refined-2') return t.navigation.leaderboard;
    if (path === '/leaderboard-refined-3') return t.navigation.leaderboard;
    if (path === '/leaderboard-activity-refined-1') return t.navigation.activityLeaderboard;
    if (path === '/leaderboard-activity-refined-2') return t.navigation.activityLeaderboard;
    if (path === '/leaderboard-activity-refined-3') return t.navigation.activityLeaderboard;
    if (path === '/leaderboard/overall-championship') return t.navigation.championship;
    if (path === '/leaderboard/most-active') return t.navigation.mostActive;
    if (path === '/leaderboard/under-handicap') return t.navigation.underHandicap;
    if (path === '/leaderboard/playing-streaks') return t.navigation.playingStreaks;
    
    // Analytics routes
    if (path === '/analytics/course-records') return t.navigation.courseRecords;
    if (path === '/analytics/activity') return t.navigation.activityAnalytics;
    if (path === '/analytics/scoring') return t.navigation.scoringAnalytics;
    if (path.includes('/analytics/')) return t.navigation.analytics;
    if (path.includes('/metric-analysis/')) return t.navigation.metricAnalysis;
    if (path === '/metric-analysis-detailed/clubs') return t.navigation.clubAnalysis;
    
    // Social and messaging routes
    if (path === '/social') return t.navigation.social;
    if (path === '/search') return t.navigation.search;
    if (path === '/notifications') return t.navigation.notifications;
    if (path === '/messages') return t.navigation.messages;
    if (path.includes('/messages/')) return t.navigation.chat;
    
    // Round and game routes

    if (path.includes('/round/') && path.includes('/analytics')) return t.navigation.roundAnalytics;
    
    // Community routes
    if (path.includes('/community/chat/')) return t.navigation.clubChat;
    
    // Debug and demo routes
    if (path === '/debug/messaging') return 'Debug';
    if (path === '/demo/trends') return 'Trends Demo';
    if (path === '/course-records/variations') return t.navigation.courseRecords;

    // Tab roots (practice-first bar)
    if (path === '/practice') return t.navigation.practice;
    if (path === '/progression') return t.navigation.progress;
    if (path.startsWith('/coaching')) return t.navigation.coach;

    // Unmapped route: no title rather than a wrong one ("Dashboard")
    return '';
  };

  const shouldShowBackButton = location.pathname !== '/dashboard';

  return (
    <header
      className="app-chrome fixed top-0 left-0 right-0 z-50 safe-area-x rounded-b-2xl"
      style={{
        background: 'rgba(255, 255, 255, 0.72)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        borderBottom: '1px solid rgba(0,0,0,0.08)',
        paddingTop: 'env(safe-area-inset-top, 0px)',
      }}
    >
      <div className="flex items-center justify-between px-4 py-2 h-14 max-w-sm mx-auto">
        {/* Left: Profile Avatar + Search */}
        <div className="flex items-center gap-1">
          <button
            data-setup="nav-profile"
            onClick={() => navigate('/profile')}
            aria-label={t.navigation.profile}
            className="p-0.5 rounded-full transition-all duration-200 active:scale-95 tap-target inline-flex items-center justify-center shrink-0"
          >
            <Avatar className="h-8 w-8" style={{ boxShadow: '0 0 0 2px #2A4494, 0 0 0 3.5px rgba(255,255,255,0.9)' }}>
              <AvatarImage src={profile?.avatar_url || undefined} />
              <AvatarFallback
                className="text-xs text-white font-semibold"
                style={{ background: 'linear-gradient(135deg, #2A4494 0%, #1E3270 100%)' }}
              >
                {/* Same name source as the Profile page avatar, so both show the same initials */}
                {getInitials(user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'U')}
              </AvatarFallback>
            </Avatar>
          </button>
          <button
            data-setup="nav-search"
            onClick={() => navigate('/search')}
            aria-label={t.common.search}
            className="p-2 rounded-xl transition-all duration-200 active:scale-95 tap-target inline-flex items-center justify-center shrink-0"
            style={{ color: 'rgba(0,0,0,0.45)' }}
          >
            <Search className="h-5 w-5" />
          </button>
        </div>

        {/* Center: Page Title */}
        <h1
          className="text-base font-bold absolute left-1/2 -translate-x-1/2 tracking-tight"
          style={{ color: '#1a1a1a' }}
        >
          {getPageTitle()}
        </h1>

        {/* Right: Messages + Notifications */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => navigate('/messages')}
            aria-label={`${t.navigation.messages}${unreadMessageCount > 0 ? `, ${unreadMessageCount}` : ''}`}
            className="p-2 rounded-xl transition-all duration-200 active:scale-95 relative tap-target inline-flex items-center justify-center shrink-0"
            style={{ color: 'rgba(0,0,0,0.45)' }}
          >
            <MessageSquare className="h-5 w-5" />
            {unreadMessageCount > 0 && (
              <span
                aria-hidden="true"
                className="absolute -top-0.5 -right-0.5 h-4 w-4 text-white text-[10px] rounded-full flex items-center justify-center font-bold"
                style={{ background: 'linear-gradient(135deg, #2A4494 0%, #1E3270 100%)', boxShadow: '0 1px 4px rgba(42,68,148,0.5)' }}
              >
                {unreadMessageCount > 9 ? '9+' : unreadMessageCount}
              </span>
            )}
          </button>
          <button
            onClick={() => navigate('/notifications')}
            aria-label={`${t.navigation.notifications}${unreadNotificationCount > 0 ? `, ${unreadNotificationCount}` : ''}`}
            className="p-2 rounded-xl transition-all duration-200 active:scale-95 relative tap-target inline-flex items-center justify-center shrink-0"
            style={{ color: 'rgba(0,0,0,0.45)' }}
          >
            <Bell className="h-5 w-5" />
            {unreadNotificationCount > 0 && (
              <span
                aria-hidden="true"
                className="absolute -top-0.5 -right-0.5 h-4 w-4 text-white text-[10px] rounded-full flex items-center justify-center font-bold"
                style={{ background: 'linear-gradient(135deg, #2A4494 0%, #1E3270 100%)', boxShadow: '0 1px 4px rgba(42,68,148,0.5)' }}
              >
                {unreadNotificationCount > 9 ? '9+' : unreadNotificationCount}
              </span>
            )}
          </button>
        </div>
      </div>
    </header>
  );
}
